import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import Container from "@mui/material/Container";
import CircularProgress from "@mui/material/CircularProgress";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";

import UserBar from "../components/UserBar";
import axios from "../components/Axios";
import ChatList from "../components/ask/ChatList";
import Composer from "../components/ask/Composer";
import EmptyState from "../components/ask/EmptyState";
import Answer from "../components/ask/Answer";
import MomentDialog from "../components/ask/MomentDialog";
import { mergeMessages, momentAt, momentFrom } from "../components/ask/askModel";
import { searchLang } from "../components/search/nlqModel";
import "../components/search/Search.css"; // the moment thumbnails and player
import "../components/ask/Ask.css";

const SIDE = "ask:side"; // localStorage: "hidden" when the chat list is folded
const POLL = 1000; // ms between looks at an answer being written
const POLL_RETRY = 3000; // after a look that failed
const LIST_POLL = 5000; // ms between looks at the chat list while one of its chats answers
const TITLE_WAIT = 2500; // ms after a new chat until its title is likely named

function stored(key) {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}
function store(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    // the choice lasts for the page only
  }
}

const nearBottom = (el) => el.scrollTop + el.clientHeight >= el.scrollHeight - 160;
const questionText = (content) => (Array.isArray(content) ? content.map((b) => b.text || "").join("") : content);

// Ask questions about the videos of the library; answers are written by the API in the background, so the page looks
// at the one being written every second, and the chat list shows which chats are still answering.
export default function Ask({ history, match }) {
  const { t, i18n } = useTranslation();
  const chatId = match.params.chat_id || null;
  const [chats, setChats] = useState([]);
  const [chat, setChat] = useState(null); // {chat_id, title, status, error, messages: [{index, role, content, status?, error?}]}
  const [missing, setMissing] = useState(false); // the chat in the address couldn't be loaded: "gone" or "failed"
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false); // sending a question
  const [failed, setFailed] = useState(false); // the last question couldn't be sent
  const [stopping, setStopping] = useState(false);
  const [sideOpen, setSideOpen] = useState(() => stored(SIDE) !== "hidden");
  const [playing, setPlaying] = useState(null); // {moments, index, highlight}
  const shown = useRef(null); // the chat shown, for the effects
  shown.current = chat;
  const stick = useRef(true); // keep the end in view after the next update
  const main = useRef(null); // the conversation's column, scrolling
  const lang = searchLang(i18n.language);
  const running = !!chat && chat.status === "running";

  const refreshChats = useCallback(
    () =>
      axios
        .get("/ai/chats", { quiet: true })
        .then((r) => setChats(r.data.chats))
        .catch((e) => console.log(e)),
    [],
  );
  useEffect(() => {
    refreshChats();
  }, [refreshChats]);

  // the chat in the address
  useEffect(() => {
    setPlaying(null);
    setFailed(false);
    setMissing(false);
    setStopping(false);
    if (!chatId) {
      setChat(null);
      return;
    }
    if (shown.current && shown.current.chat_id === chatId) return; // just started here
    setChat(null);
    let live = true;
    axios
      .get("/ai/chats/" + chatId)
      .then((r) => {
        if (!live) return;
        stick.current = true;
        setChat(r.data);
      })
      .catch((e) => live && setMissing(e.response && e.response.status === 404 ? "gone" : "failed"));
    return () => {
      live = false;
    };
  }, [chatId]);

  // while it answers, the answer as it is written
  const id = chat && chat.chat_id;
  useEffect(() => {
    if (!running) return;
    let live = true;
    let timer;
    const look = (delay) => {
      timer = setTimeout(() => {
        const messages = shown.current.messages;
        const start = messages.length ? messages[messages.length - 1].index : 0;
        axios
          .get("/ai/chats/" + id, { params: { start }, quiet: true })
          .then((r) => {
            if (!live) return;
            stick.current = nearBottom(main.current);
            setChat((prev) => (prev && prev.chat_id === r.data.chat_id ? { ...r.data, messages: mergeMessages(prev.messages, r.data.messages) } : prev));
            if (r.data.status === "running") look(POLL);
          })
          .catch(() => live && look(POLL_RETRY));
      }, delay);
    };
    look(POLL);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [running, id]);

  // an answer done: the list shows it, with the chat's title once named
  const wasRunning = useRef(false);
  useEffect(() => {
    if (wasRunning.current && !running) {
      setStopping(false);
      refreshChats();
    }
    wasRunning.current = running;
  }, [running, refreshChats]);

  // the other chats answering
  const anyRunning = chats.some((c) => c.status === "running");
  useEffect(() => {
    if (!anyRunning) return;
    const timer = setInterval(refreshChats, LIST_POLL);
    return () => clearInterval(timer);
  }, [anyRunning, refreshChats]);

  useLayoutEffect(() => {
    if (!chat || !stick.current) return;
    main.current.scrollTop = main.current.scrollHeight;
    stick.current = false;
  }, [chat]);

  const options = (extra) => ({ lang, ...extra });
  // the chat from what a request returned: its messages from the first returned on
  const update = (r) => {
    stick.current = true;
    setChat((prev) => ({ ...r.data, messages: mergeMessages(prev ? prev.messages : [], r.data.messages) }));
    refreshChats();
  };

  const ask = (question) => {
    const message = question.trim();
    if (!message || busy || running) return;
    setBusy(true);
    setFailed(false);
    const request = chat ? axios.post("/ai/chats/" + chat.chat_id + "/messages", options({ message })) : axios.post("/ai/chats", options({ message }));
    request
      .then((r) => {
        setText("");
        if (chat) return update(r);
        stick.current = true;
        shown.current = r.data;
        setChat(r.data);
        history.push("/ask/" + r.data.chat_id);
        refreshChats();
        setTimeout(refreshChats, TITLE_WAIT);
      })
      .catch((e) => {
        console.log(e);
        setFailed(true);
      })
      .finally(() => setBusy(false));
  };

  const stop = () => {
    setStopping(true);
    axios.post("/ai/chats/" + chat.chat_id + "/stop").catch(() => setStopping(false));
  };

  const retry = () => {
    setFailed(false);
    axios
      .post("/ai/chats/" + chat.chat_id + "/retry", options())
      .then(update)
      .catch((e) => {
        console.log(e);
        setFailed(true);
      });
  };

  const rename = (c, title) =>
    axios
      .put("/ai/chats/" + c.chat_id, { title })
      .then((r) => {
        setChat((prev) => (prev && prev.chat_id === c.chat_id ? { ...prev, title: r.data.title } : prev));
        refreshChats();
      })
      .catch((e) => console.log(e));

  const remove = (c) =>
    axios
      .delete("/ai/chats/" + c.chat_id)
      .then(() => {
        if (c.chat_id === chatId) history.push("/ask");
        refreshChats();
      })
      .catch((e) => console.log(e));

  const toggleSide = () => {
    store(SIDE, sideOpen ? "hidden" : "shown");
    setSideOpen(!sideOpen);
  };

  const play = useCallback((moments, index, highlight) => setPlaying({ moments, index, highlight }), []);
  // a cited moment: the answer's moment there, else the 10 s from the time cited
  const cite = useCallback((turn, { projectId, time }) => {
    const m = momentAt(turn.moments, projectId, time);
    if (m) setPlaying({ moments: turn.moments, index: turn.moments.indexOf(m), highlight: turn.highlight });
    else setPlaying({ moments: [...turn.moments, momentFrom(projectId, time, turn.names)], index: turn.moments.length, highlight: turn.highlight });
  }, []);

  const composer = (rows, placeholder) => (
    <Composer
      text={text}
      onText={setText}
      onSend={() => ask(text)}
      onStop={stop}
      running={running}
      stopping={stopping}
      busy={busy}
      rows={rows}
      placeholder={placeholder}
      autoFocus
    />
  );
  const sendFailed = failed && (
    <p className="ak-failed" role="alert">
      {t("The question couldn’t be sent: try again.", { nsSeparator: false })}
    </p>
  );

  let body;
  if (missing) {
    body = (
      <main className="ak-conversation is-empty">
        <div className="ak-missing">
          <h2 className="ak-empty-title">{missing === "gone" ? t("This chat doesn’t exist anymore") : t("This chat couldn’t be loaded")}</h2>
          {missing === "gone" ? <Link to="/ask">{t("Start a new chat")}</Link> : <span>{t("Reload the page, or try again in a moment.")}</span>}
        </div>
      </main>
    );
  } else if (!chatId) {
    body = (
      <main className="ak-conversation is-empty">
        <EmptyState composer={<>{composer(3, t("Ask about your videos"))}{sendFailed}</>} />
      </main>
    );
  } else if (!chat) {
    body = (
      <main className="ak-conversation">
        <CircularProgress className="ak-loading" />
      </main>
    );
  } else {
    const last = chat.messages.length - 1;
    body = (
      <>
        <main className="ak-conversation">
          {chat.messages.map((m, i) =>
            m.role === "user" ? (
              <div key={m.index} className="ak-question">
                <p>{questionText(m.content)}</p>
              </div>
            ) : (
              <Answer key={m.index} message={m} last={i === last} onRetry={retry} onPlay={play} onCite={cite} />
            ),
          )}
        </main>
        <div className="ak-dock">
          {composer(1, t("Ask a follow-up"))}
          {sendFailed}
        </div>
      </>
    );
  }

  return (
    <>
      <UserBar themed title="Ask" subtitle={<Trans>about your videos</Trans>} docTitle={chat ? chat.title : undefined} />
      <div className="askPage">
        <Container maxWidth="lg" className="ak-body">
          <ChatList chats={chats} current={chatId} open={sideOpen} onToggle={toggleSide} onRename={rename} onDelete={remove} />
          <div ref={main} className="ak-main">
            {body}
          </div>
        </Container>
      </div>
      {playing && <MomentDialog playing={playing} onIndex={(i) => setPlaying((p) => ({ ...p, index: i }))} onClose={() => setPlaying(null)} />}
    </>
  );
}
